import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import dayjs from 'dayjs'
import * as echarts from 'echarts/core'
import { GraphChart, ScatterChart } from 'echarts/charts'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Refreshing from 'components/core/Refreshing'
import EChart from '../Cost/EChart'
import { Tile } from '../Cost/parts'
import { fmtCount } from '../StreamIngest/vocabulary'
import { TOOLTIP, axisStyle, useChartStyle } from './chartStyle'

echarts.use([GraphChart, ScatterChart])

// The Friends tab: does being connected, and being heard, go with listening
// (reclip-api `stream/metrics/loop/friends.py`, after the proto console's
// fleet tab). Each person whose phone reported, at their friends now (x)
// against their days with a listen (y), in the accent when somebody else heard
// one of their quotes and grey when nobody has yet: emphasis, one group the
// point and the other its context. If the social loop works, the accent sits
// higher; each group's median is its line. People at the same point are one
// mark, sized by how many. The table under it is the chart's twin.
//
// Above it, who hears whom (`network` on the same read): everyone on a
// friendship or a listen, the quotes between them as arrows from whose quote it
// was to who heard it, in the accent; friendships nothing has travelled along
// yet dashed in grey. Emphasis again, the accent the point: a dot is accent
// when somebody else heard one of that person's quotes, hollow when their phone
// does not report. Its twin is the pairs table under it.
//
// The page reads this once (`useLoopReads`, `friends`) for the loop panel's
// Friends stage and this tab alike.

const MAX_ROWS = 50

// `hollow` draws the dot as a ring, as the network draws a phone that does
// not report.
const Key = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
    {items.map(({ label, color, hollow = false }) => (
      <Box
        key={label}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            boxSizing: 'border-box',
            ...(hollow
              ? { border: `1.5px solid ${color}` }
              : { bgcolor: color }),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
)

// People at one point of one group as one mark: its size grows with them.
const pointsOf = (people) => {
  const at = new Map()
  people.forEach((person) => {
    const key = `${person.friends}|${person.days_heard}`
    if (!at.has(key)) at.set(key, [])
    at.get(key).push(person)
  })
  return [...at.values()].map((group) => ({
    value: [group[0].friends, group[0].days_heard],
    people: group,
    symbolSize: 8 + 4 * Math.sqrt(group.length - 1),
  }))
}

const Scatter = ({ people, medians }) => {
  const style = useChartStyle()
  const option = useMemo(() => {
    const heard = pointsOf(people.filter(({ heard_by }) => heard_by))
    const notYet = pointsOf(people.filter(({ heard_by }) => !heard_by))
    // Each group's median, its label at its own end of the line, so two
    // medians at one height never print over each other.
    const median = (value, label, position) =>
      value == null
        ? undefined
        : {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'solid', width: 1, opacity: 0.7 },
            label: {
              formatter: `${label} ${value}`,
              color: style.text,
              fontSize: 10,
              position,
            },
            data: [{ yAxis: value }],
          }
    const series = (name, data, color, line) => ({
      name,
      type: 'scatter',
      data,
      // A ring of the panel, so overlapping marks stay two.
      itemStyle: {
        color,
        borderColor: style.surface,
        borderWidth: 2,
      },
      markLine: line && { ...line, lineStyle: { ...line.lineStyle, color } },
    })
    return {
      grid: { left: 40, right: 24, top: 16, bottom: 36 },
      tooltip: {
        trigger: 'item',
        ...TOOLTIP,
        formatter: ({ data }) => {
          const names = data.people
            .slice(0, 5)
            .map((p) => p.name || p.user_id.slice(-6))
            .join(', ')
          const more =
            data.people.length > 5 ? ` and ${data.people.length - 5} more` : ''
          return `${data.value[0]} friend${
            data.value[0] === 1 ? '' : 's'
          } · heard a quote on ${data.value[1]} day${
            data.value[1] === 1 ? '' : 's'
          }<br/><span style="opacity:.75">${names}${more}</span>`
        },
      },
      xAxis: {
        type: 'value',
        minInterval: 1,
        name: 'friends',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { color: style.text, fontSize: 10 },
        ...axisStyle(style),
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        name: 'days with a listen',
        nameTextStyle: { color: style.text, fontSize: 10 },
        ...axisStyle(style),
      },
      series: [
        series(
          'nobody has yet',
          notYet,
          style.muted,
          median(medians.not_yet, 'median, not yet', 'insideStartBottom')
        ),
        series(
          'somebody else heard one of their quotes',
          heard,
          style.accent,
          median(medians.heard, 'median, heard', 'insideEndTop')
        ),
      ],
    }
  }, [people, medians, style])

  return (
    <>
      <Key
        items={[
          {
            label: 'somebody else heard one of their quotes',
            color: style.accent,
          },
          { label: 'nobody has yet', color: style.muted },
        ]}
      />
      <EChart
        option={option}
        height={320}
        isEmpty={people.length === 0}
        emptyText="No phone reported in this window."
      />
    </>
  )
}

const nameOf = (node, id) => node?.name || id.slice(-6)

const Network = ({ network }) => {
  const style = useChartStyle()
  const desktop = useMediaQuery((theme) => theme.breakpoints.up('desktop'))
  const option = useMemo(() => {
    const byId = new Map(network.nodes.map((node) => [node.user_id, node]))
    const name = (id) => nameOf(byId.get(id), id)
    const links = network.edges.map((edge) => {
      const heard = edge.a_heard_b + edge.b_heard_a
      // An arrow points from whose quote it was to who heard it.
      const [source, target] =
        edge.a_heard_b && !edge.b_heard_a ? [edge.b, edge.a] : [edge.a, edge.b]
      return {
        source,
        target,
        edge,
        symbol: !heard
          ? ['none', 'none']
          : edge.a_heard_b && edge.b_heard_a
          ? ['arrow', 'arrow']
          : ['none', 'arrow'],
        lineStyle: heard
          ? {
              color: style.accent,
              width: 1.5 + Math.min(3, Math.sqrt(heard)),
              type: edge.friends ? 'solid' : 'dotted',
              opacity: 0.9,
            }
          : { color: style.muted, width: 1, type: 'dashed', opacity: 1 },
      }
    })
    return {
      tooltip: {
        trigger: 'item',
        ...TOOLTIP,
        formatter: ({ dataType, data }) => {
          if (dataType === 'edge') {
            const { a, b, friends, a_heard_b: ab, b_heard_a: ba } = data.edge
            return `${name(a)} heard ${ab} of ${name(b)}’s quotes<br/>${name(
              b
            )} heard ${ba} of ${name(a)}’s<br/><span style="opacity:.75">${
              friends ? 'friends' : 'not friends'
            }</span>`
          }
          const node = byId.get(data.id)
          return `${nameOf(node, data.id)}<br/><span style="opacity:.75">${
            node.reports ? 'phone reports' : 'phone doesn’t report'
          } · ${node.heard_by} heard their quotes</span>`
        },
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          roam: true,
          draggable: true,
          // Pulled in enough that a tester cohort settles inside the box,
          // tighter on a phone's narrow one.
          force: desktop
            ? { repulsion: 170, edgeLength: [55, 120], gravity: 0.15 }
            : { repulsion: 110, edgeLength: [40, 90], gravity: 0.25 },
          edgeSymbolSize: 7,
          emphasis: { focus: 'adjacency', lineStyle: { width: 3 } },
          label: {
            show: true,
            position: 'right',
            color: style.text,
            fontSize: 10,
          },
          data: network.nodes.map((node) => ({
            id: node.user_id,
            name: nameOf(node, node.user_id),
            symbolSize: 14 + 4 * Math.sqrt(node.heard_by),
            itemStyle: node.reports
              ? {
                  color: node.heard_by ? style.accent : style.muted,
                  borderColor: style.surface,
                  borderWidth: 2,
                }
              : {
                  color: style.surface,
                  borderColor: style.muted,
                  borderWidth: 1.5,
                },
          })),
          links,
        },
      ],
    }
  }, [network, style, desktop])

  return (
    <>
      <Key
        items={[
          {
            label: 'a quote reached the other (the arrow: who heard it)',
            color: style.accent,
          },
          { label: 'friends, nothing heard yet (dashed)', color: style.muted },
          {
            label: 'their phone doesn’t report',
            color: style.muted,
            hollow: true,
          },
        ]}
      />
      <EChart
        option={option}
        height={460}
        isEmpty={network.edges.length === 0}
        emptyText="No friendship or listen between people in this window."
      />
    </>
  )
}

// The network's twin: each pair, most heard first.
const Pairs = ({ network }) => {
  const byId = new Map(network.nodes.map((node) => [node.user_id, node]))
  const rows = [...network.edges].sort(
    (x, y) => y.a_heard_b + y.b_heard_a - (x.a_heard_b + x.b_heard_a)
  )
  return (
    <Box sx={{ overflowX: 'auto', mt: 2 }}>
      <Table size="small" sx={{ minWidth: 560 }}>
        <TableHead>
          <TableRow>
            <TableCell>A · B</TableCell>
            <TableCell>friends</TableCell>
            <TableCell align="right">A heard B’s</TableCell>
            <TableCell align="right">B heard A’s</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, MAX_ROWS).map((edge) => (
            <TableRow key={`${edge.a}|${edge.b}`}>
              <TableCell>
                {nameOf(byId.get(edge.a), edge.a)} ·{' '}
                {nameOf(byId.get(edge.b), edge.b)}
              </TableCell>
              <TableCell>{edge.friends ? 'yes' : 'no'}</TableCell>
              <TableCell align="right">{fmtCount(edge.a_heard_b)}</TableCell>
              <TableCell align="right">{fmtCount(edge.b_heard_a)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > MAX_ROWS && (
        <Typography variant="caption" color="text.secondary">
          The {MAX_ROWS} pairs who heard the most, of {fmtCount(rows.length)}.
        </Typography>
      )}
    </Box>
  )
}

const People = ({ people }) => (
  <Box sx={{ overflowX: 'auto', mt: 2 }}>
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>person</TableCell>
          <TableCell align="right">friends</TableCell>
          <TableCell align="right">days with a listen</TableCell>
          <TableCell align="right">heard by others</TableCell>
          <TableCell align="right">their own heard</TableCell>
          <TableCell align="right">others’ heard</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {people.slice(0, MAX_ROWS).map((p) => (
          <TableRow key={p.user_id}>
            <TableCell>
              <Link component={RouterLink} to={`/users?user=${p.user_id}`}>
                {p.name || p.user_id}
              </Link>
            </TableCell>
            <TableCell align="right">{fmtCount(p.friends)}</TableCell>
            <TableCell align="right">{fmtCount(p.days_heard)}</TableCell>
            <TableCell align="right">{fmtCount(p.heard_by)}</TableCell>
            <TableCell align="right">{fmtCount(p.heard_own)}</TableCell>
            <TableCell align="right">{fmtCount(p.heard_others)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {people.length > MAX_ROWS && (
      <Typography variant="caption" color="text.secondary">
        The {MAX_ROWS} who heard on the most days, of {fmtCount(people.length)}.
      </Typography>
    )}
  </Box>
)

const Friends = ({ reads }) => {
  const {
    friends: answer,
    friendsError: error,
    friendsLoading: loading,
  } = reads
  if (error)
    return (
      <Alert severity="warning">
        The event table could not be read: {error}
      </Alert>
    )
  const summary = answer?.summary
  return (
    <Refreshing active={loading}>
      {answer && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Tile
              label="Phones reporting"
              value={fmtCount(summary.people)}
              help="People whose phones reported anything in the window: everyone below."
            />
            <Tile
              label="With a friend"
              value={fmtCount(summary.with_friends)}
              help="Of them, people with at least one accepted friend now."
            />
            <Tile
              label="Heard by someone else"
              value={fmtCount(summary.heard_by_someone)}
              help="Of them, people one of whose quotes somebody else heard in the window."
            />
            <Tile
              label="New friendships"
              value={fmtCount(summary.befriended)}
              help="Friendships accepted in the window, each pair once."
            />
            {summary.friendships != null && (
              <Tile
                label="A quote reached a friend"
                value={`${fmtCount(summary.friendships_heard)} / ${fmtCount(
                  summary.friendships
                )}`}
                help="Friendships where one friend heard the other’s quotes in the window, of all friendships with a phone that reports."
              />
            )}
          </Box>
          {answer.network && (
            <Paper
              id="network"
              variant="outlined"
              sx={{
                p: { mobile: 2, desktop: 3 },
                borderRadius: 2,
                mb: 3,
                scrollMarginTop: 24,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Who hears whom
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, maxWidth: 900 }}
              >
                Everyone on a friendship or a listen this window. Drag to
                rearrange, scroll to zoom, hover a line for who heard what.
                {answer.network.truncated
                  ? ' Only the most connected fit.'
                  : ''}
              </Typography>
              <Network network={answer.network} />
              <Pairs network={answer.network} />
            </Paper>
          )}
          <Paper
            variant="outlined"
            sx={{ p: { mobile: 2, desktop: 3 }, borderRadius: 2, mb: 3 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Does being connected, and being heard, go with listening?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, maxWidth: 900 }}
            >
              Each person whose phone reported, at their friends now against the
              days they heard a quote. If the social loop works, the people
              others hear sit higher. Hover a mark for who is there.
              {answer.read_at
                ? ` Read ${dayjs(answer.read_at).format(
                    'HH:mm'
                  )}, kept five minutes.`
                : ''}
              {answer.truncated
                ? ' Only the most engaged fit in one read.'
                : ''}
            </Typography>
            <Scatter people={answer.people} medians={answer.medians} />
            <People people={answer.people} />
          </Paper>
        </>
      )}
    </Refreshing>
  )
}

export default Friends
